/**
 * Selector de plantillas de email del tenant.
 *
 *   GET /api/v1/email-templates                 → lista de kinds + variants + selección actual
 *   GET /api/v1/email-templates/preview?...     → HTML del preview (lo cargamos en iframe)
 *   PATCH /api/v1/email-templates/selection     → persiste la elección
 *
 * UX:
 *   - Tabs por kind (invitación, confirmación de orden, etc.)
 *   - Por cada variant: card con preview + botón Seleccionar
 *   - Selected variant marcado con check
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Card, Badge, PageHeader, Button } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth, tenantHeaders } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import { Mail, Check, Loader2, ExternalLink } from 'lucide-react'

export const Route = createFileRoute('/store/$id/email-templates')({
  component: EmailTemplatesPage,
})

interface VariantInfo {
  variant: string
  label: string
  description: string
}

interface KindInfo {
  kind: string
  label: string
  description: string
  defaultVariant: string
  selectedVariant: string
  variants: VariantInfo[]
}

function EmailTemplatesPage() {
  const qc = useQueryClient()
  const [activeKind, setActiveKind] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => tenantApi.get<{ data: KindInfo[] }>('/api/v1/email-templates'),
  })

  const kinds = data?.data ?? []
  const current = kinds.find((k) => k.kind === activeKind) ?? kinds[0]

  // Auto-seleccionar el primer kind cuando carga
  useEffect(() => {
    if (!activeKind && kinds.length > 0) setActiveKind(kinds[0]!.kind)
  }, [activeKind, kinds])

  const select = useMutation({
    mutationFn: (input: { kind: string; variant: string }) =>
      tenantApi.patch('/api/v1/email-templates/selection', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-templates'] }),
  })

  return (
    <>
      <PageHeader
        title="Plantillas de email"
        description="Elegí el estilo visual de los correos automáticos que tu tienda envía."
      />
      <div className="p-8 space-y-6">
        {isLoading && <p className="text-sm text-neutral-400">Cargando...</p>}
        {error && (
          <Card className="p-4 bg-red-900/20 border-red-800">
            <p className="text-sm text-red-400">{(error as Error).message}</p>
          </Card>
        )}

        {kinds.length > 0 && (
          <div className="flex gap-2 border-b border-neutral-800">
            {kinds.map((k) => (
              <button
                key={k.kind}
                onClick={() => setActiveKind(k.kind)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition',
                  (activeKind ?? kinds[0]?.kind) === k.kind
                    ? 'text-white border-blue-500'
                    : 'text-neutral-500 border-transparent hover:text-neutral-300',
                )}
              >
                <Mail className="size-4" />
                {k.label}
              </button>
            ))}
          </div>
        )}

        {current && (
          <KindEditor
            kind={current}
            onSelect={(variant) => select.mutate({ kind: current.kind, variant })}
            saving={select.isPending}
          />
        )}
      </div>
    </>
  )
}

function KindEditor({
  kind, onSelect, saving,
}: {
  kind: KindInfo
  onSelect: (variant: string) => void
  saving: boolean
}) {
  const [previewVariant, setPreviewVariant] = useState<string>(kind.selectedVariant)

  // Cuando cambia el kind activo, resetear el preview a la selección actual
  useEffect(() => {
    setPreviewVariant(kind.selectedVariant)
  }, [kind.kind, kind.selectedVariant])

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
      {/* Lista de variants */}
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-wider text-neutral-500 font-medium">{kind.description}</div>
        {kind.variants.map((v) => {
          const isSelected = kind.selectedVariant === v.variant
          const isPreview = previewVariant === v.variant
          return (
            <Card
              key={v.variant}
              className={cn(
                'p-4 cursor-pointer transition',
                isPreview && !isSelected && 'border-blue-500 ring-1 ring-blue-500/30',
                isSelected && 'border-emerald-700 bg-emerald-950/10',
                !isPreview && !isSelected && 'hover:border-neutral-700',
              )}
              onClick={() => setPreviewVariant(v.variant)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm">{v.label}</p>
                    {isSelected && <Badge variant="success">Activo</Badge>}
                    {kind.defaultVariant === v.variant && !isSelected && <Badge variant="muted">Default</Badge>}
                  </div>
                  <p className="text-xs text-neutral-500 mt-1 leading-relaxed">{v.description}</p>
                </div>
                {isSelected && <Check className="size-5 text-emerald-400 shrink-0" />}
              </div>
              {isPreview && !isSelected && (
                <Button
                  size="sm"
                  className="w-full mt-3"
                  onClick={(e) => {
                    e.stopPropagation()
                    onSelect(v.variant)
                  }}
                  disabled={saving}
                >
                  {saving ? <><Loader2 className="size-4 mr-2 animate-spin" /> Guardando...</> : 'Usar esta plantilla'}
                </Button>
              )}
            </Card>
          )
        })}
      </div>

      {/* Preview iframe */}
      <PreviewPane kind={kind.kind} variant={previewVariant} />
    </div>
  )
}

function PreviewPane({ kind, variant }: { kind: string; variant: string }) {
  const { apiUrl } = useAuth()
  const [html, setHtml] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const url = useMemo(
    () => `${apiUrl.replace(/\/$/, '')}/api/v1/email-templates/preview?kind=${encodeURIComponent(kind)}&variant=${encodeURIComponent(variant)}`,
    [apiUrl, kind, variant],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErr(null)
    void fetch(url, { credentials: 'include', headers: tenantHeaders() })
      .then(async (res) => {
        const text = await res.text()
        if (cancelled) return
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        setHtml(text)
      })
      .catch((e) => { if (!cancelled) setErr(e instanceof Error ? e.message : 'Error') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [url])

  return (
    <Card className="p-0 overflow-hidden bg-neutral-900 min-h-[600px] flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-800 bg-neutral-950/50">
        <span className="text-xs font-mono text-neutral-500">Preview · {variant}</span>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-neutral-500 hover:text-neutral-300 inline-flex items-center gap-1"
        >
          Abrir en nueva pestaña <ExternalLink className="size-3" />
        </a>
      </div>
      <div className="flex-1 relative bg-white">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-neutral-500">
            <Loader2 className="size-5 animate-spin" />
          </div>
        )}
        {err && (
          <div className="absolute inset-0 flex items-center justify-center text-red-400 text-sm p-4">{err}</div>
        )}
        {html && (
          <iframe
            title="Email preview"
            srcDoc={html}
            sandbox="allow-same-origin"
            className="w-full h-[800px] border-0 block"
          />
        )}
      </div>
    </Card>
  )
}
